import React from 'react'
import { Button, Tag, Typography } from 'antd'
import { UnorderedListOutlined } from '@ant-design/icons'
import classNames from 'classnames'
import { ERecieveMethod, ITableOrder } from 'models/orders'
import { ListInfoItem } from 'components/orders/ListInfoItem/ListInfoItem'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { getSignedPrice } from 'shared/utils/getSignedPrice'
import { getDiscountPayerLabel } from 'shared/utils/orders/getDiscountPayerLabel'
import {
  getOrderMoneyBreakdown,
  getPvzDeliveryRecalc
} from 'shared/utils/orders/getOrderMoneyBreakdown'
import styles from './OrderEdit.module.scss'

interface IOrderPriceSectionProps {
  order: ITableOrder
  commission: number
  deliveryPrice?: string
  deliveryType?: ERecieveMethod
  isPickUpPoint: boolean
  onOpenTransactionsModal: () => void
}

export const OrderPriceSection: React.FC<IOrderPriceSectionProps> = ({
  order,
  commission,
  deliveryPrice,
  deliveryType,
  isPickUpPoint,
  onOpenTransactionsModal
}) => {
  const courierDeliverySum =
    deliveryType === ERecieveMethod.PICKUP_POINT || !Number(deliveryPrice)
      ? 0
      : Number(deliveryPrice)
  const money = getOrderMoneyBreakdown(order, commission, courierDeliverySum)
  const deliveryRecalc = getPvzDeliveryRecalc(order)

  return (
    <div
      className={classNames(styles.detailsItem, styles.priceInfoContainer, {
        [styles.priceInfoContainerWithCDEK]: !isPickUpPoint
      })}
      style={{ width: isPickUpPoint ? 'calc(100% / 2)' : 'calc(100% / 3)' }}
    >
      <div className={styles.priceInfoWrapper}>
        <Typography.Title className={styles.detailsItemTitle} level={3}>
          Стоимость
        </Typography.Title>
        {order.promo_details?.is_applied && (
          <ListInfoItem
            name='Промокод'
            value={
              <Tag color='green' style={{ marginRight: 0 }}>
                {order.promo_details.promo_code ?? '—'}
              </Tag>
            }
          />
        )}
        <ListInfoItem
          name='Стоимость товаров'
          value={getNormalizedPrice(order.sum)}
        />
        <ListInfoItem
          name='Комиссия площадки'
          value={`${!!commission ? '-' : ''}${getNormalizedPrice(commission)}`}
        />
        {order.promo_details?.is_applied && (
          <>
            <ListInfoItem
              name='Цена до скидки'
              value={getNormalizedPrice(order.promo_details.original_sum ?? 0)}
            />
            <ListInfoItem
              name='Цена после скидки'
              value={getNormalizedPrice(order.promo_details.discounted_sum ?? 0)}
            />
            <ListInfoItem
              name='Скидка'
              value={getNormalizedPrice(order.promo_details.discount_amount ?? 0)}
            />
            <ListInfoItem
              name='Скидку оплатил'
              value={getDiscountPayerLabel(order.promo_details)}
            />
          </>
        )}
        <Typography.Title className={styles.subsectionTitle} level={5}>
          Доставка
        </Typography.Title>
        {deliveryRecalc && (
          <ListInfoItem
            name='Расчёт на ПВЗ'
            value={`${getNormalizedPrice(deliveryRecalc.factCost)} (${deliveryRecalc.diff > 0 ? '+' : '-'} ${getNormalizedPrice(Math.abs(deliveryRecalc.diff))})`}
            wrapValue
          />
        )}
        <ListInfoItem
          name='За счёт площадки'
          value={getNormalizedPrice(money.platformDeliverySum)}
        />
        <ListInfoItem
          name='За счёт продавца'
          value={getNormalizedPrice(money.sellerDeliverySum)}
        />
        <ListInfoItem
          name='Доплатил покупатель'
          value={getNormalizedPrice(money.buyerDeliverySum)}
        />
        <Typography.Title className={styles.subsectionTitle} level={5}>
          Итог
        </Typography.Title>
        <ListInfoItem
          name='Для покупателя'
          value={getNormalizedPrice(money.buyerTotal)}
        />
        <ListInfoItem
          name='Для продавца'
          value={getNormalizedPrice(money.sellerPayout)}
        />
        <ListInfoItem
          name='Заработок площадки'
          value={getSignedPrice(money.platformProfit)}
          valueStyle={{ color: money.platformProfit < 0 ? '#cf1322' : '#389e0d' }}
        />
        <Button
          block
          size='middle'
          htmlType='button'
          icon={<UnorderedListOutlined />}
          style={{ marginTop: 25 }}
          onClick={onOpenTransactionsModal}
        >
          Транзакции заказа
        </Button>
      </div>
    </div>
  )
}
